import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactCountryFlag from 'react-country-flag';
import MenuIcon from '@mui/icons-material/Menu';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import { ApiConstants } from '../../main/constants/ApiConstants';
import { buildEntityFileUrl } from '../../main/utils/fileUrl';
import '../../main/theme/theme.css';
import './PublicProductMenuDetails.css';
import i18n from '../../main/initializers/i18n';

type ProductMenuPublicItemDto = {
  productId: number;
  name: string;
  price: number;
  descriptionAl?: string | null;
  descriptionEn?: string | null;
  descriptionIt?: string | null;
  productImageContent?: string | null;
  isPrefered: boolean;
  order: number;
  isActive: boolean;
  categoryId?: number | null;
  categoryName?: string | null;
};

type PublicMenuCompanyDataDto = {
  companyId: number;
  companyName?: string | null;
  email?: string | null;
  phone?: string | null;
  location?: string | null;
  website?: string | null;
  currencyId?: number | null;
  languageId?: number | null;
  exchangeRateEur: number;
  exchangeRatePound: number;
  currencies?: PublicMenuCurrencyDto[] | null;
};

type PublicMenuCurrencyDto = {
  id?: number | null;
  code: string;
  symbol?: string | null;
  value: number;
};

type PublicMenuEventDto = {
  title?: string | null;
  description?: string | null;
  startsAt?: string | null;
};

type PublicMenuCategoryDto = {
  categoryId?: number | null;
  name: string;
  productCount: number;
};

type PublicProductMenuResponseDto = {
  companyData: PublicMenuCompanyDataDto;
  categories?: PublicMenuCategoryDto[] | null;
  preferedItems: ProductMenuPublicItemDto[];
  items: ProductMenuPublicItemDto[];
  event?: PublicMenuEventDto | null;
};

type AppTheme = 'light' | 'dark' | 'ocean' | 'pearl' | 'forest' | 'sky';

const THEMES: AppTheme[] = ['light', 'sky', 'dark', 'ocean', 'pearl', 'forest'];
const SUPPORTED_LANGUAGES = ['al', 'en', 'it'] as const;
const LANGUAGE_BY_ID: Record<number, (typeof SUPPORTED_LANGUAGES)[number]> = { 1: 'al', 2: 'en', 3: 'it' };
const LANGUAGE_FLAG_MAP: Record<(typeof SUPPORTED_LANGUAGES)[number], string> = { al: 'AL', en: 'GB', it: 'IT' };
const LANGUAGE_LABEL_MAP: Record<(typeof SUPPORTED_LANGUAGES)[number], string> = { al: 'Shqip', en: 'English', it: 'Italiano' };

const publicApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

const normalizeText = (v: string) => v.trim().toLowerCase();

const PublicProductMenuDetails = () => {
  const { menuKeyCode } = useParams();
  const normalizedMenuKeyCode = (menuKeyCode || '').trim();

  const [menuResponse, setMenuResponse] = useState<PublicProductMenuResponseDto | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<ProductMenuPublicItemDto | null>(null);
  const [isEventPopupOpen, setIsEventPopupOpen] = useState(false);
  const [isContactPopupOpen, setIsContactPopupOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isCurrencyDropdownOpen, setIsCurrencyDropdownOpen] = useState(false);
  const [isThemeDropdownOpen, setIsThemeDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [selectedCategoryKey, setSelectedCategoryKey] = useState('');
  const [hasAppliedCompanyDefaults, setHasAppliedCompanyDefaults] = useState(false);

  const contactMenuRef = useRef<HTMLDivElement | null>(null);
  const languageMenuRef = useRef<HTMLDivElement | null>(null);
  const currencyMenuRef = useRef<HTMLDivElement | null>(null);
  const themeMenuRef = useRef<HTMLDivElement | null>(null);
  const categorySectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const preferedSliderViewportRef = useRef<HTMLDivElement | null>(null);
  const preferedSliderDragRef = useRef({ isDragging: false, startX: 0, startScrollLeft: 0 });

  const currentLanguage = (i18n.language || 'en').split('-')[0];
  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    SUPPORTED_LANGUAGES.includes(currentLanguage as (typeof SUPPORTED_LANGUAGES)[number]) ? currentLanguage : 'en',
  );
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [theme, setTheme] = useState<AppTheme>(() => {
    const stored = window.localStorage.getItem('publicMenuTheme');
    return stored && THEMES.includes(stored as AppTheme) ? (stored as AppTheme) : 'light';
  });

  useEffect(() => {
    if (!normalizedMenuKeyCode) {
      setError(i18n.t('menuDetails.invalidLicenseKey', { defaultValue: 'Invalid menu key.' }));
      setLoading(false);
      return;
    }

    const loadMenu = async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await publicApi.get<PublicProductMenuResponseDto>(ApiConstants.productMenuDetails.menuByKey, {
          params: { menuKeyCode: normalizedMenuKeyCode },
        });
        setMenuResponse(res.data);
      } catch (e: any) {
        const msg =
          e?.response?.data?.message ||
          e?.response?.data?.Message ||
          e?.response?.data?.error ||
          e?.message ||
          i18n.t('menuDetails.failedToLoadProducts', { defaultValue: 'Failed to load products.' });
        setError(String(msg));
        setMenuResponse(null);
      } finally {
        setLoading(false);
      }
    };

    void loadMenu();
  }, [normalizedMenuKeyCode]);

  useEffect(() => {
    window.localStorage.setItem('publicMenuTheme', theme);
  }, [theme]);

  useEffect(() => {
    setHasAppliedCompanyDefaults(false);
  }, [normalizedMenuKeyCode]);

  useEffect(() => {
    const normalized = (i18n.language || 'en').split('-')[0];
    if (SUPPORTED_LANGUAGES.includes(normalized as (typeof SUPPORTED_LANGUAGES)[number])) {
      setSelectedLanguage(normalized);
    }
  }, [i18n.language]);

  useEffect(() => {
    if (!menuResponse?.event) return;
    setIsEventPopupOpen(true);
  }, [menuResponse?.event]);

  useEffect(() => {
    if (!selectedProduct && !isEventPopupOpen && !isContactPopupOpen && !isLanguageDropdownOpen && !isCurrencyDropdownOpen && !isThemeDropdownOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedProduct(null);
        setIsEventPopupOpen(false);
        setIsContactPopupOpen(false);
        setIsLanguageDropdownOpen(false);
        setIsCurrencyDropdownOpen(false);
        setIsThemeDropdownOpen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedProduct, isEventPopupOpen, isContactPopupOpen, isLanguageDropdownOpen, isCurrencyDropdownOpen, isThemeDropdownOpen]);

  useEffect(() => {
    const handleOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (contactMenuRef.current && !contactMenuRef.current.contains(target)) setIsContactPopupOpen(false);
      if (languageMenuRef.current && !languageMenuRef.current.contains(target)) setIsLanguageDropdownOpen(false);
      if (currencyMenuRef.current && !currencyMenuRef.current.contains(target)) setIsCurrencyDropdownOpen(false);
      if (themeMenuRef.current && !themeMenuRef.current.contains(target)) setIsThemeDropdownOpen(false);
    };

    window.addEventListener('mousedown', handleOutside);
    return () => window.removeEventListener('mousedown', handleOutside);
  }, []);

  const companyData = menuResponse?.companyData;
  const companyName = companyData?.companyName || i18n.t('menuDetails.companyNameFallback', { defaultValue: 'Our Company' });
  const companyLogoUrl = buildEntityFileUrl(companyData?.companyId, ApiConstants.EntityTypes.Company);
  const getProductImageUrl = (product?: ProductMenuPublicItemDto | null) =>
    product?.productImageContent || buildEntityFileUrl(product?.productId, ApiConstants.EntityTypes.Product);

  const orderedProducts = useMemo(
    () =>
      [...(menuResponse?.items || [])]
        .filter((p) => p.isActive !== false)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.name.localeCompare(b.name)),
    [menuResponse?.items],
  );

  const preferedItems = useMemo(
    () =>
      [...(menuResponse?.preferedItems || [])]
        .filter((p) => p.isActive !== false)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.name.localeCompare(b.name)),
    [menuResponse?.preferedItems],
  );

  const availableCurrencies = useMemo(() => {
    const fromTable = (companyData?.currencies || [])
      .filter((c) => c?.code && c.value > 0)
      .map((c) => ({
        id: c.id ?? null,
        code: c.code.toUpperCase(),
        symbol: c.symbol?.trim() || null,
        value: Number(c.value),
      }));

    if (fromTable.length) return fromTable;

    return [
      { id: 0, code: 'ALL', symbol: 'L', value: 1 },
      { id: 0, code: 'EUR', symbol: 'EUR', value: Number(companyData?.exchangeRateEur || 98) },
      { id: 0, code: 'GBP', symbol: 'GBP', value: Number(companyData?.exchangeRatePound || 114) },
    ];
  }, [companyData?.currencies, companyData?.exchangeRateEur, companyData?.exchangeRatePound]);

  const companyDefaultCurrencyCode = useMemo(() => {
    if (typeof companyData?.currencyId !== 'number') return null;
    const defaultCurrency = availableCurrencies.find((c) => c.id === companyData.currencyId);
    return defaultCurrency?.code || null;
  }, [companyData?.currencyId, availableCurrencies]);

  const baseCurrencyCode = companyDefaultCurrencyCode || availableCurrencies[0]?.code || 'ALL';
  const activeCurrencyCode = selectedCurrency || companyDefaultCurrencyCode || availableCurrencies[0]?.code || 'ALL';

  useEffect(() => {
    if (!companyData || hasAppliedCompanyDefaults) return;

    const languageFromCompany = typeof companyData.languageId === 'number' ? LANGUAGE_BY_ID[companyData.languageId] : undefined;
    if (languageFromCompany) {
      setSelectedLanguage(languageFromCompany);
      void i18n.changeLanguage(languageFromCompany);
      localStorage.setItem('i18nextLng', languageFromCompany);
    }

    if (companyDefaultCurrencyCode) setSelectedCurrency(companyDefaultCurrencyCode);
    setHasAppliedCompanyDefaults(true);
  }, [companyData, companyDefaultCurrencyCode, hasAppliedCompanyDefaults]);

  useEffect(() => {
    if (!selectedCurrency) return;
    if (!availableCurrencies.some((c) => c.code === selectedCurrency)) {
      setSelectedCurrency(availableCurrencies[0]?.code || 'ALL');
    }
  }, [availableCurrencies, selectedCurrency]);

  const resolveDescription = (product?: ProductMenuPublicItemDto | null) => {
    if (!product) return '';
    if (selectedLanguage === 'al') return product.descriptionAl || product.descriptionEn || product.descriptionIt || '';
    if (selectedLanguage === 'it') return product.descriptionIt || product.descriptionEn || product.descriptionAl || '';
    return product.descriptionEn || product.descriptionAl || product.descriptionIt || '';
  };

  const formatPrice = (value: number) => {
    const baseCurrency = availableCurrencies.find((c) => c.code === baseCurrencyCode);
    const targetCurrency = availableCurrencies.find((c) => c.code === activeCurrencyCode);
    const baseRate = baseCurrency?.value && baseCurrency.value > 0 ? baseCurrency.value : 1;
    const targetRate = targetCurrency?.value && targetCurrency.value > 0 ? targetCurrency.value : 1;
    const converted = value * (baseRate / targetRate);

    const localeByLanguage: Record<string, string> = { al: 'sq-AL', en: 'en-GB', it: 'it-IT' };
    const locale = localeByLanguage[selectedLanguage] || 'en-GB';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: activeCurrencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(converted);
    } catch {
      return `${converted.toFixed(2)} ${activeCurrencyCode}`;
    }
  };

  const onLanguageChange = (language: string) => {
    setSelectedLanguage(language);
    void i18n.changeLanguage(language);
    localStorage.setItem('i18nextLng', language);
  };

  const categories = useMemo(() => {
    const otherLabel = i18n.t('menuDetails.otherCategory', { defaultValue: 'Other' });
    const map = new Map<string, { key: string; label: string; count: number; isOther: boolean; items: ProductMenuPublicItemDto[] }>();

    for (const category of menuResponse?.categories || []) {
      const label = (category.name || '').trim() || otherLabel;
      const key = category.categoryId != null ? `ID:${category.categoryId}` : `NAME:${label.toLowerCase()}`;
      map.set(key, {
        key,
        label,
        count: 0,
        isOther: label.toLowerCase() === otherLabel.toLowerCase(),
        items: [],
      });
    }

    for (const p of orderedProducts) {
      const label = (p.categoryName || '').trim() || otherLabel;
      const key = p.categoryId != null ? `ID:${p.categoryId}` : `NAME:${label.toLowerCase()}`;
      const existing = map.get(key);
      if (existing) {
        existing.count += 1;
        existing.items.push(p);
      } else {
        map.set(key, { key, label, count: 1, isOther: label.toLowerCase() === otherLabel.toLowerCase(), items: [p] });
      }
    }

    return Array.from(map.values()).sort((a, b) => {
      if (a.isOther && !b.isOther) return 1;
      if (!a.isOther && b.isOther) return -1;
      return a.label.localeCompare(b.label);
    });
  }, [menuResponse?.categories, orderedProducts, selectedLanguage]);

  useEffect(() => {
    if (!categories.length) {
      setSelectedCategoryKey('');
      return;
    }
    if (!selectedCategoryKey || !categories.some((category) => category.key === selectedCategoryKey)) {
      setSelectedCategoryKey(categories[0].key);
    }
  }, [categories, selectedCategoryKey]);

  const filteredCategorySections = useMemo(() => {
    const q = normalizeText(searchText);
    return categories
      .map((category) => ({
        ...category,
        items: category.items.filter((product) => {
          if (!q) return true;
          const hay = `${product.name} ${resolveDescription(product)} ${category.label}`.toLowerCase();
          return hay.includes(q);
        }),
      }))
      .filter((category) => category.items.length > 0);
  }, [categories, searchText, selectedLanguage]);

  useEffect(() => {
    if (!filteredCategorySections.length) return;
    if (!filteredCategorySections.some((category) => category.key === selectedCategoryKey)) {
      setSelectedCategoryKey(filteredCategorySections[0].key);
    }
  }, [filteredCategorySections, selectedCategoryKey]);

  useEffect(() => {
    if (!filteredCategorySections.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target instanceof HTMLElement) {
          const key = visible.target.dataset.categoryKey;
          if (key) setSelectedCategoryKey(key);
        }
      },
      { rootMargin: '-20% 0px -55% 0px', threshold: [0.2, 0.35, 0.5] },
    );

    filteredCategorySections.forEach((category) => {
      const element = categorySectionRefs.current[category.key];
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [filteredCategorySections]);

  const handleCategorySelect = (categoryKey: string) => {
    setSelectedCategoryKey(categoryKey);
    categorySectionRefs.current[categoryKey]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handlePreferedSliderPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const viewport = preferedSliderViewportRef.current;
    if (!viewport) return;
    const target = e.target as HTMLElement | null;
    if (target?.closest('.public-menu-featured-card')) return;

    preferedSliderDragRef.current = {
      isDragging: true,
      startX: e.clientX,
      startScrollLeft: viewport.scrollLeft,
    };
    viewport.setPointerCapture?.(e.pointerId);
  };

  const handlePreferedSliderPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const viewport = preferedSliderViewportRef.current;
    const drag = preferedSliderDragRef.current;
    if (!viewport || !drag.isDragging) return;
    viewport.scrollLeft = drag.startScrollLeft - (e.clientX - drag.startX);
  };

  const stopPreferedSliderDrag = (pointerId?: number) => {
    const viewport = preferedSliderViewportRef.current;
    preferedSliderDragRef.current.isDragging = false;
    if (viewport && typeof pointerId === 'number' && viewport.hasPointerCapture?.(pointerId)) {
      viewport.releasePointerCapture(pointerId);
    }
  };

  if (loading) {
    return <div className="public-menu-state">{i18n.t('menuDetails.loadingMenu', { defaultValue: 'Loading menu...' })}</div>;
  }

  if (error) {
    return <div className="public-menu-state public-menu-state-error">{error}</div>;
  }

  return (
    <div className={`public-menu-page bcShell bcTheme-${theme}`}>
      <div className="public-menu-backdrop public-menu-backdrop--one" />
      <div className="public-menu-backdrop public-menu-backdrop--two" />

      <header className="public-menu-hero">
        <div className="public-menu-hero__copy">
          <div className="public-menu-hero__brand">
            {companyData?.companyId ? (
              <img src={companyLogoUrl} alt={companyName} className="public-menu-logo" />
            ) : (
              <div className="public-menu-logo-placeholder">{companyName.charAt(0)}</div>
            )}

            <div>
              <h1 className="public-menu-title">{companyName}</h1>
            </div>
          </div>
        </div>

        <div className="public-menu-hero__actions">
          <label className="public-menu-search public-menu-search--inline" aria-label={i18n.t('menuDetails.search', { defaultValue: 'Search' })}>
            <SearchIcon className="public-menu-search-icon" fontSize="small" />
            <input
              className="public-menu-search-input"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={i18n.t('menuDetails.search', { defaultValue: 'Search' })}
            />
            {searchText ? (
              <button type="button" className="public-menu-search-clear" onClick={() => setSearchText('')}>
                x
              </button>
            ) : null}
          </label>

          <Link to="/" className="public-menu-icon-btn" aria-label={i18n.t('menuDetails.home', { defaultValue: 'Home' })}>
            <HomeOutlinedIcon fontSize="small" />
          </Link>

          <div className="theme-dropdown public-menu-theme-dropdown" ref={themeMenuRef}>
            <button
              className="theme-dropdown-trigger public-menu-control-btn"
              onClick={() => {
                setIsThemeDropdownOpen((prev) => !prev);
                setIsLanguageDropdownOpen(false);
                setIsCurrencyDropdownOpen(false);
              }}
              type="button"
            >
              <PaletteOutlinedIcon fontSize="small" />
              <span>{i18n.t('mainLanding.theme.label', { defaultValue: 'Theme' })}: {theme}</span>
            </button>

            {isThemeDropdownOpen ? (
              <div className="theme-dropdown-menu public-menu-dropdown-menu">
                {THEMES.map((themeOption) => (
                  <button
                    key={themeOption}
                    className={`theme-dropdown-item ${theme === themeOption ? 'active' : ''}`}
                    onClick={() => {
                      setTheme(themeOption);
                      setIsThemeDropdownOpen(false);
                    }}
                    type="button"
                  >
                    <span className={`theme-dot theme-dot--${themeOption}`} />
                    <span>{themeOption}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div className="public-menu-language-dropdown" ref={languageMenuRef}>
            <button
              type="button"
              className="public-menu-control-btn"
              aria-expanded={isLanguageDropdownOpen}
              aria-label={i18n.t('menuDetails.language', { defaultValue: 'Language' })}
              onClick={() => {
                setIsLanguageDropdownOpen((prev) => !prev);
                setIsThemeDropdownOpen(false);
                setIsCurrencyDropdownOpen(false);
              }}
            >
              <ReactCountryFlag svg countryCode={LANGUAGE_FLAG_MAP[selectedLanguage as (typeof SUPPORTED_LANGUAGES)[number]]} />
              <span>{selectedLanguage.toUpperCase()}</span>
            </button>

            {isLanguageDropdownOpen ? (
              <div className="public-menu-dropdown-menu">
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    className={`public-menu-dropdown-item ${selectedLanguage === lang ? 'is-active' : ''}`}
                    onClick={() => {
                      onLanguageChange(lang);
                      setIsLanguageDropdownOpen(false);
                    }}
                  >
                    <ReactCountryFlag svg countryCode={LANGUAGE_FLAG_MAP[lang]} />
                    <span>{LANGUAGE_LABEL_MAP[lang]}</span>
                    <strong>{lang.toUpperCase()}</strong>
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div className="public-menu-currency-dropdown" ref={currencyMenuRef}>
            <button
              type="button"
              className="public-menu-control-btn"
              aria-expanded={isCurrencyDropdownOpen}
              aria-label={i18n.t('menuDetails.currency', { defaultValue: 'Currency' })}
              onClick={() => {
                setIsCurrencyDropdownOpen((prev) => !prev);
                setIsThemeDropdownOpen(false);
                setIsLanguageDropdownOpen(false);
              }}
            >
              <span>{activeCurrencyCode}</span>
            </button>

            {isCurrencyDropdownOpen ? (
              <div className="public-menu-dropdown-menu public-menu-dropdown-menu--right">
                {availableCurrencies.map((currency) => (
                  <button
                    key={currency.code}
                    type="button"
                    className={`public-menu-dropdown-item ${activeCurrencyCode === currency.code ? 'is-active' : ''}`}
                    onClick={() => {
                      setSelectedCurrency(currency.code);
                      setIsCurrencyDropdownOpen(false);
                    }}
                  >
                    <span>{currency.code}</span>
                    <strong>{currency.symbol || currency.code}</strong>
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div ref={contactMenuRef} className="public-menu-contact-wrap">
            <button
              type="button"
              className="public-menu-icon-btn"
              onClick={() => setIsContactPopupOpen((prev) => !prev)}
              aria-expanded={isContactPopupOpen}
              aria-label={i18n.t('menuDetails.companyDetails', { defaultValue: 'Company details' })}
            >
              <MenuIcon fontSize="small" />
            </button>

            {isContactPopupOpen ? (
              <div className="public-menu-contact-popup">
                <p className="public-menu-contact-title">{i18n.t('menuDetails.contact', { defaultValue: 'Contact' })}</p>
                <p>{companyData?.location || i18n.t('menuDetails.address', { defaultValue: 'Address not available' })}</p>
                <p>{companyData?.phone || i18n.t('menuDetails.phone', { defaultValue: 'Phone not available' })}</p>
                <p>{companyData?.email || i18n.t('menuDetails.email', { defaultValue: 'Email not available' })}</p>
              </div>
            ) : null}
          </div>
        </div>

        <div className="public-menu-toolbar" />
      </header>

      <div className="public-menu-layout">
        {filteredCategorySections.length ? (
          <aside className="public-menu-sidebar">
            <div className="public-menu-sidebar__inner">
              {filteredCategorySections.map((category) => (
                <button
                  key={category.key}
                  type="button"
                  className={`public-menu-category-link ${selectedCategoryKey === category.key ? 'is-active' : ''}`}
                  onClick={() => handleCategorySelect(category.key)}
                >
                  <span>{category.label}</span>
                  <strong>{category.items.length}</strong>
                </button>
              ))}
            </div>
          </aside>
        ) : null}

        <main className="public-menu-content">
          {preferedItems.length ? (
            <section className="public-menu-featured">
              <div className="public-menu-section-head">
                <p className="public-menu-section-kicker">{i18n.t('menuDetails.preferedItems', { defaultValue: 'Preferred items' })}</p>
              </div>

              <div
                ref={preferedSliderViewportRef}
                className="public-menu-featured__viewport"
                onPointerDown={handlePreferedSliderPointerDown}
                onPointerMove={handlePreferedSliderPointerMove}
                onPointerUp={(e) => stopPreferedSliderDrag(e.pointerId)}
                onPointerCancel={(e) => stopPreferedSliderDrag(e.pointerId)}
                onPointerLeave={(e) => stopPreferedSliderDrag(e.pointerId)}
              >
                <div className="public-menu-featured__track">
                  {[...preferedItems, ...preferedItems].map((item, index) => (
                    <button
                      key={`${item.productId}-${index}`}
                      type="button"
                      className="public-menu-featured-card"
                      onClick={() => setSelectedProduct(item)}
                    >
                      <div className="public-menu-featured-card__imageWrap">
                        {getProductImageUrl(item) ? (
                          <img
                            src={getProductImageUrl(item)}
                            alt={item.name}
                            className="public-menu-featured-card__image"
                            loading="eager"
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          <div className="public-menu-image-placeholder">{i18n.t('menuDetails.noPhoto', { defaultValue: 'No photo' })}</div>
                        )}
                      </div>

                      <div className="public-menu-featured-card__body">
                        <strong>{item.name}</strong>
                        <span>{formatPrice(item.price)}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </section>
          ) : null}

          {!filteredCategorySections.length ? (
            <div className="public-menu-state">{i18n.t('menuDetails.noProductsAvailable', { defaultValue: 'No products available.' })}</div>
          ) : (
            <div className="public-menu-sections">
              {filteredCategorySections.map((category) => (
                <section
                  key={category.key}
                  className="public-menu-section"
                  data-category-key={category.key}
                  ref={(element) => {
                    categorySectionRefs.current[category.key] = element;
                  }}
                >
                  <div className="public-menu-section-head">
                    <div className='itemcategoryclass'>
                      <p className="public-menu-section-kicker">{i18n.t('menuDetails.category', { defaultValue: 'Category' })}</p>
                      <h2>{category.label}</h2>
                    </div>
                  </div>

                  <div className="public-menu-grid">
                    {category.items.map((product) => (
                      <article key={product.productId} className="public-menu-card">
                        <button
                          type="button"
                          className="public-menu-card-button"
                          onClick={() => setSelectedProduct(product)}
                          aria-label={i18n.t('menuDetails.openProductDetails', { defaultValue: 'Open product details' })}
                        >
                          <div className="public-menu-image-wrap">
                            {getProductImageUrl(product) ? (
                              <img
                                src={getProductImageUrl(product)}
                                alt={product.name}
                                className="public-menu-image"
                                loading="eager"
                                referrerPolicy="no-referrer"
                              />
                            ) : (
                              <div className="public-menu-image-placeholder">{i18n.t('menuDetails.noPhoto', { defaultValue: 'No photo' })}</div>
                            )}
                          </div>

                          <div className="public-menu-card-body">
                            <h3>{product.name}</h3>
                            <div className="public-menu-card-footer">
                              <span className="public-menu-price">{formatPrice(product.price)}</span>
                            </div>
                          </div>
                        </button>
                      </article>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </main>
      </div>

      {selectedProduct ? (
        <div className="public-menu-modal-overlay" role="presentation" onClick={() => setSelectedProduct(null)}>
          <div
            className="public-menu-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="public-menu-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="public-menu-modal-close"
              aria-label={i18n.t('menuDetails.close', { defaultValue: 'Close details' })}
              onClick={() => setSelectedProduct(null)}
            >
              x
            </button>

            <div className="public-menu-modal-image-wrap">
              {getProductImageUrl(selectedProduct) ? (
                <img
                  src={getProductImageUrl(selectedProduct)}
                  alt={selectedProduct.name}
                  className="public-menu-modal-image"
                  loading="lazy"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="public-menu-image-placeholder">{i18n.t('menuDetails.noPhoto', { defaultValue: 'No photo' })}</div>
              )}
            </div>

            <div className="public-menu-modal-body">
              <div className="public-menu-modal-top">
                <p className="public-menu-section-kicker">{selectedProduct.categoryName || i18n.t('menuDetails.menu', { defaultValue: 'Menu' })}</p>
                <h2 id="public-menu-modal-title">{selectedProduct.name}</h2>
                <span className="public-menu-price">{formatPrice(selectedProduct.price)}</span>
              </div>

              <p className="public-menu-modal-description">
                {resolveDescription(selectedProduct) || i18n.t('menuDetails.noDescription', { defaultValue: 'No description available.' })}
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {isEventPopupOpen && menuResponse?.event ? (
        <div className="public-menu-modal-overlay public-menu-event-overlay" role="presentation" onClick={() => setIsEventPopupOpen(false)}>
          <div className="public-menu-modal public-menu-event-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className="public-menu-modal-close"
              aria-label={i18n.t('menuDetails.close', { defaultValue: 'Close details' })}
              onClick={() => setIsEventPopupOpen(false)}
            >
              x
            </button>

            <div className="public-menu-modal-body">
              <p className="public-menu-eyebrow">{i18n.t('menuDetails.event', { defaultValue: 'Event' })}</p>
              <h2>{menuResponse.event.title || i18n.t('menuDetails.todayEvent', { defaultValue: 'Upcoming event' })}</h2>
              {menuResponse.event.startsAt ? <p className="public-menu-modal-description">{menuResponse.event.startsAt}</p> : null}
              {menuResponse.event.description ? <p className="public-menu-modal-description">{menuResponse.event.description}</p> : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PublicProductMenuDetails;
